// AutomationRuleModal.jsx

import React from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Hooks
import useAutomationRuleModalState from './hooks/useAutomationRuleModalState';

// CSS
import './AutomationRuleModal.css';

// Components
import AutomationRuleEditor from './AutomationRuleEditor';
import AutomationRuleModalFooter from './AutomationRuleModalFooter';
import Modal from 'components/modal/Modal';
import ModalForm from 'components/modal/form/ModalForm';

/**
 * AutomationRuleModal
 *
 * Renders a modal editor for a single automation rule.
 *
 * @returns {ReactNode} The rendered component.
 */
const AutomationRuleModal = React.memo(({
  allowEmailActions = true,
  fieldOptions,
  fieldTypeByKey,
  fieldEnumOptionsByKey,
  isOpen,
  isSaving = false,
  mode = 'create',
  nodeTypeOptions,
  onDelete,
  onSave,
  rule,
  ruleFieldOptions,
  setOpen,
  baseKey,
}) => {

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- HOOKS ----- //

  const {
    handlers,
    modalState,
  } = useAutomationRuleModalState({
    fieldTypeByKey,
    isOpen,
    onClose: () => setOpen(false),
    onDelete,
    onSave,
    rule,
    ruleFieldOptions,
  });

  const { draftRule } = modalState;
  const {
    handleAddAction,
    handleAddCondition,
    handleClose,
    handleDelete,
    handleRemoveAction,
    handleRemoveCondition,
    handleSave,
    updateConditionRows,
    updateRule,
  } = handlers;

  const canSave = !isSaving && Boolean(draftRule?.key);

  // ----- RENDER ----- //

  return (

    <Modal
      iconKey="bolt"
      title={mode === 'edit' ? 'Edit Automation' : 'Add Automation'}
      isOpen={isOpen}
      width={isMobile ? '100%' : 600}
      height="auto"
      maxHeight="88vh"
      onClose={handleClose}
    >
      <ModalForm>

        <AutomationRuleEditor
          allowEmailActions={allowEmailActions}
          draftRule={draftRule}
          fieldEnumOptionsByKey={fieldEnumOptionsByKey}
          fieldOptions={fieldOptions}
          fieldTypeByKey={fieldTypeByKey}
          handleAddAction={handleAddAction}
          handleAddCondition={handleAddCondition}
          handleRemoveAction={handleRemoveAction}
          handleRemoveCondition={handleRemoveCondition}
          nodeTypeOptions={nodeTypeOptions}
          ruleFieldOptions={ruleFieldOptions}
          updateConditionRows={updateConditionRows}
          updateRule={updateRule}
          baseKey={baseKey}
        />

      </ModalForm>

      <AutomationRuleModalFooter
        mode={mode}
        isSaving={isSaving}
        canSave={canSave}
        onCancel={handleClose}
        onDelete={handleDelete}
        onSave={handleSave}
      />

    </Modal>
  );
});

// Dev Tools
AutomationRuleModal.displayName = 'AutomationRuleModal';

export default AutomationRuleModal;
